import React, {PropsWithChildren, ReactElement, ReactNode, useEffect, useRef, useState} from "react";
import {createPortal} from "react-dom";

export enum TooltipLocation {
    TOP = 'top',
    LEFT = 'left',
    BOTTOM = 'bottom',
    RIGHT = 'right',
    AUTO = 'auto',
}

export type Props = {
    tooltip: ReactNode,
    location?: TooltipLocation,
    fullWidth?: boolean,
}

export default function WithTooltip({ tooltip, children, location = TooltipLocation.AUTO, ...props }: PropsWithChildren<Props>): ReactElement {
    const [toolTipActive, setToolTipActive] = useState(false);
    const [parentBoundingBox, setParentBoundingBox] = useState(null);
    const containerRef = useRef<HTMLDivElement>(null);

    function mouseEnterHandler(event: React.MouseEvent<HTMLDivElement>): void {
        setParentBoundingBox(containerRef.current.getBoundingClientRect());
        setToolTipActive(true);
    }

    return <>
        {toolTipActive ? <Tooltip parentBoundingBox={parentBoundingBox} location={location}>{tooltip}</Tooltip> : null}
        <div ref={containerRef} className={'bt-with-tooltip ' + (props.fullWidth ? 'full-width' : '')} onMouseEnter={mouseEnterHandler} onMouseLeave={() => setToolTipActive(false)}>
            {children}
        </div>
    </>
}

type Position = {
    top?: number,
    left?: number,
    right?: number,
}

function Tooltip({ parentBoundingBox, location, children }) {
    const [position, setPosition] = useState<Position>(null);
    const [tooltipSize, setTooltipSize] = useState<{width: number, height: number}>(null);
    const tipDiv = useRef<HTMLDivElement>();

    function calculateLocation(target: HTMLDivElement): void {
        if (target === null) {
            return;
        }

        const ownBoundingBox = target.getBoundingClientRect();

        if (tooltipSize === null) {
            setTooltipSize({width: ownBoundingBox.width, height: ownBoundingBox.height});
        }

        let newPosition: Position = { };
        switch(location) {
            case TooltipLocation.TOP:
            default:
                newPosition.top = parentBoundingBox.top - ownBoundingBox.height;
                newPosition.left = Math.round((parentBoundingBox.left + parentBoundingBox.right - ownBoundingBox.width) / 2);
                break;
            case TooltipLocation.BOTTOM:
                newPosition.top = parentBoundingBox.bottom;
                newPosition.left = Math.round((parentBoundingBox.left + parentBoundingBox.right - ownBoundingBox.width) / 2);
                break;
            case TooltipLocation.RIGHT:
                newPosition.top = parentBoundingBox.top + parentBoundingBox.height / 2 - ownBoundingBox.height / 2;
                newPosition.left = parentBoundingBox.right + 16;
                break;
        }

        const maxLeft = document.body.getBoundingClientRect().right - (tooltipSize?.width ?? 150) - 16;
        newPosition.left = Math.max(16, Math.min(maxLeft, newPosition.left));
        setPosition(newPosition);
    }

    useEffect(() => {
        calculateLocation(tipDiv.current);
    }, [tipDiv, tipDiv.current?.getBoundingClientRect()?.height, tooltipSize])

    return createPortal(
        <div style={{ opacity: position === null ? 0 : 1, ...(position ?? { top: 0, left: 0 }) }} className={'bt-tooltip'} ref={tipDiv}>
            {children}
        </div>,
        document.body,
    );
}
